'use client'

import { useEffect } from 'react'

// error.tsx (in this same directory) catches errors thrown while rendering a
// route's content — but NOT errors thrown by the root layout itself (the
// <html>/<body> shell, AuthProvider/StripeProvider, the analytics scripts).
// That's what this file is for: Next.js's App Router convention for a
// root-layout-level error boundary. It only ever activates when the layout
// itself is broken, which should be rare — but when it happens, error.tsx
// can't help (it's rendered *inside* the layout that just failed), so this
// has to render its own complete <html>/<body> from scratch and can't rely
// on anything from layout.tsx (providers, fonts, nav) since that's exactly
// what's broken. Deliberately minimal/dependency-free so this fallback can't
// itself fail the same way.
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    console.error('[CVCircuit] Unhandled error in root layout:', error)
    // The console.error above is effectively unobserved in practice: this
    // fires a hard `window.location.href` navigation below, which clears
    // the browser console in most setups before anyone could read it —
    // meaning every occurrence of this boundary so far has left zero
    // retrievable diagnostic data. A prior investigation (logging out while
    // a page that reads `user` is still mounted was the leading theory)
    // checked every `user.<property>` access across the app and found them
    // all already null-guarded, so the real cause is still unconfirmed.
    // (Since found, and it was never this boundary's to catch: see the note
    // above the error capture in AuthProvider.)
    // Persisting the actual error + the last known route BEFORE the
    // redirect (see AuthProvider's own pathname-breadcrumb effect) is what
    // static code reading can't substitute for — next occurrence, check
    // localStorage['cvcircuit:lastGlobalError'] in devtools for the real
    // message/stack/route instead of guessing again.
    try {
      const lastPathname = sessionStorage.getItem('cvcircuit:lastPathname')
      localStorage.setItem('cvcircuit:lastGlobalError', JSON.stringify({
        message: error.message,
        stack: error.stack,
        digest: error.digest,
        lastPathname,
        timestamp: new Date().toISOString(),
      }))
    } catch {
      // ignore — diagnostic capture is best-effort, must never block the redirect below
    }
    // A layout-level crash here is most commonly a stale render caught
    // mid-transition (e.g. logging out while a page that reads `user` is
    // still mounted) rather than something the visitor can meaningfully
    // "try again" from — reset() re-renders the same broken tree, and this
    // boundary can't rely on next/navigation since the layout that would
    // normally provide routing context is exactly what just failed. A hard
    // reload of "/" sidesteps both: fresh document, fresh providers, no
    // stale state left over from whatever just threw. Kept brief (not
    // instant) so the message is at least visible if this ever fires for a
    // genuinely unexpected reason, and the manual buttons below still work
    // immediately if someone acts before the timer.
    const id = setTimeout(() => { window.location.href = '/' }, 1200)
    return () => clearTimeout(id)
  }, [error])

  return (
    <html lang="en">
      <body style={{ margin: 0, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        <div style={{
          minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: '#f9fafb', padding: '1rem',
        }}>
          <div style={{ textAlign: 'center', maxWidth: '28rem' }}>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', marginBottom: '0.5rem' }}>
              Something went wrong
            </h1>
            <p style={{ color: '#4b5563', marginBottom: '1.5rem' }}>
              CVCircuit hit an unexpected error loading the page. Try again, or head back to the homepage.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem' }}>
              <button
                onClick={() => reset()}
                style={{
                  fontWeight: 600, borderRadius: '0.5rem', padding: '0.5rem 1rem',
                  backgroundColor: '#2563eb', color: '#fff', border: 'none', cursor: 'pointer',
                }}
              >
                Try again
              </button>
              <a
                href="/"
                style={{
                  fontWeight: 600, borderRadius: '0.5rem', padding: '0.5rem 1rem',
                  border: '2px solid #2563eb', color: '#2563eb', textDecoration: 'none',
                }}
              >
                Go to Homepage
              </a>
            </div>
            {error.digest && (
              <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '1.5rem' }}>Reference: {error.digest}</p>
            )}
          </div>
        </div>
      </body>
    </html>
  )
}
